import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import PubNub from 'pubnub';
import { Row, Col, Spinner, Form } from 'react-bootstrap';
import _ from 'lodash';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import DateTimePicker from 'components/inputs/DateTimePicker';
import AlertItem from 'components/general/AlertItem';
import Footer from 'components/general/Footer';
import ReactPagination from 'components/general/ReactPagination';
import { SkeletonTheme } from 'react-loading-skeleton';
import { FETCH_ALERTS, DOWNLOAD_ALERTS } from 'actions/alerts';
import pop from 'assets/audio/Pop.mp3';
import { SiGooglesheets } from 'react-icons/si';
// import { AiFillCaretDown } from 'react-icons/ai';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import { getTimezoneFromCountryCode } from 'utils/timezone/timezoneUtils';
import Select from 'components/inputs/Select';
// Configuration options
const ALERT_TYPES = [
  { label: 'All Alerts', value: 'All' },
  { label: 'Low Wallet Balance', value: 'Low Wallet Balance' },
  { label: 'Power Loss', value: 'Power Loss' },
  { label: 'Emergency Button Pressed', value: 'Emergency Button Pressed' },
  { label: 'WebSocket Disconnected', value: 'Websocket Disconnected' },
  { label: 'Voltage Variance', value: 'Voltage Variance' },
  { label: 'Door Open', value: 'Door Open' },
  { label: 'Firmware Update', value: 'Firmware Update' },
  { label: 'Zero Unit Consumed', value: '0 unit consumed' },
  { label: 'Invoice on Hold', value: 'Invoice on Hold' },
  { label: 'Faulty Value Received', value: 'Faulty Value Received' },
  { label: 'Incorrect Attempts', value: 'Incorrect Attempt' },
  { label: 'Charging Interruption', value: 'Charging Interruption' },
  { label: 'Repeated OTP Requests', value: 'Repeated OTP Requests' },
  { label: 'Payment Failed', value: 'Payment Failed' },
  { label: 'User No Show', value: 'User No Show' },
  { label: 'First Session Failed', value: 'First Session Failed' },
  { label: 'Open Session', value: 'Open Session' },
  { label: 'Charger Fault', value: 'Charger Fault' },
  { label: 'Preparing → Finishing', value: 'Preparing to Finishing' },
  { label: 'Heat Issue', value: 'Heat Issue' },
  { label: 'Under Maintenance', value: 'Under Maintenance' },
  { label: 'Power Loss at Booked Charger', value: 'Power Loss at Booked Charger' },
  { label: 'Frequent Websocket Disconnect', value: 'Frequent Websocket Disconnect' },
];

const Alerts = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const audioRef = useRef(new Audio(pop));

  // Redux state
  const { page, total: totalData, limit, totalPages, isLoading: isLoader, allowedAlertTypes } = useSelector((state) => state.alerts);
  const currentUserName = useSelector((state) => _.get(state, 'profile.userProfile.name', ''));
  const tenantId = useSelector((state) => _.get(state, 'profile.userProfile.tenant.id', ''));
  const countryCode = useSelector((state) => _.get(state, 'profile.country_code', 'UTC'));

  // Local state
  const [localAlerts, setLocalAlerts] = useState([]);
  const [liveModeAlerts, setLiveModeAlerts] = useState([]);
  const countryTimezone = getTimezoneFromCountryCode(countryCode);
  const [startDate, setStartDate] = useState(moment(new Date()).tz(countryTimezone).startOf('day'));
  const [endDate, setEndDate] = useState(moment(new Date()).tz(countryTimezone).endOf('day'));
  const [fieldValue, setFieldValue] = useState('All Alerts');
  const [type, setType] = useState('');

  const [hover, setHover] = useState(false);
  const [isRealTimeActive, setIsRealTimeActive] = useState(false);
  const [liveUpdates, /*setLiveUpdates*/] = useState({});
  const [newAlerts, setNewAlerts] = useState(new Set());
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Create immediate update function
  const handleRealtimeUpdate = useCallback((data) => {
    if (isLiveRef.current) {
      setLiveModeAlerts(prevAlerts => {
        const existingIndex = prevAlerts.findIndex(alert => alert.id === data.id);
        const timestamp = Date.now();

        if (existingIndex !== -1) {
          const newAlerts = [...prevAlerts];
          newAlerts[existingIndex] = {
            ...newAlerts[existingIndex],
            ...data,
            timestamp
          };
          return newAlerts.sort((a, b) => b.timestamp - a.timestamp);
        }

        // Only add new alerts if they match current filters
        const newAlert = { ...data, timestamp };
        const filteredAlerts = filterAlerts([newAlert]);

        if (filteredAlerts.length > 0) {
          return [newAlert, ...prevAlerts];
        }

        return prevAlerts;
      });
    }

    setLastUpdate(Date.now());
  }, [type, startDate, endDate]);

  // Add new ref for tracking live status
  const isLiveRef = useRef(isRealTimeActive);
  const webSocketRef = useRef(null);
  // Update live toggle effect
  useEffect(() => {
    isLiveRef.current = isRealTimeActive;
  }, [isRealTimeActive]);


  // PubNub initialization
  const pubnub = React.useMemo(
    () =>
      new PubNub({
        publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY,
        subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY,
        userId: 'myUniqueUserId',
        logVerbosity: false,
        ssl: true,
      }),
    []
  );

  // Fetch alerts with filters
  const getAlerts = useCallback(
    (value = {}, isExcel = false) => {
      const baseParams = {
        from: moment(startDate).tz(countryTimezone).utc().format(),
        to: moment(endDate).tz(countryTimezone).utc().format(),
        tenantId,
        ...(type && type !== 'All' && { alertType: type }),
        ...(isExcel && { excel: true }),
        ...value,
      };

      if (baseParams.excel) {
        dispatch({
          type: DOWNLOAD_ALERTS.REQUEST,
          payload: baseParams,
          cb: (res) => {
            if (res && _.get(res, 'status') === 200) {

              if (baseParams.excel) {
                fileDownload(res.data, `${baseParams.from.format('DD/MM/YYYY')} to ${baseParams.to.format('DD/MM/YYYY')} Alerts.xlsx`);
                toast.success("File Downloaded Successfully");
              }
            }
          },
        });
      }
      else {
        dispatch({
          type: FETCH_ALERTS.REQUEST,
          payload: baseParams,
          cb: (res) => {
            if (res && _.get(res, 'status') === 200) {
              if (isLiveRef.current) {
                // In live mode, store the initial data separately
                setLiveModeAlerts(res.data.results);
              } else {
                // In normal mode, update localAlerts
                setLocalAlerts(res.data.results);
              }
            }
          }
        });
      }
    },
    [dispatch, startDate, endDate, isRealTimeActive, tenantId, type, countryTimezone]
  );

  useEffect(() => {
    if (isRealTimeActive) {
      // When live mode is activated, fetch the complete filtered data
      const filters = {
        page: 1,
        ...(type && type !== 'All' && { alertType: type }),
        ...(startDate &&
          endDate && {
          from: moment(startDate).tz(countryTimezone).utc(),
          to: moment(endDate).tz(countryTimezone).utc(),
        }),
      };
      getAlerts(filters);
    } else {
      // Clear live mode alerts when live mode is turned off
      setLiveModeAlerts([]);
    }
  }, [isRealTimeActive, type, startDate, endDate, countryTimezone]);

  // Real-time updates handling
  const handleNewAlert = useCallback(
    (newAlert) => {
      // Check if the alert is a valid object
      if (!_.isObject(newAlert)) {
        console.warn('Received invalid alert:', newAlert);
        return;
      }

      // Check if tenant ID matches the current user's tenant ID
      if (newAlert.tenantId === tenantId) {
        // Only add new alerts if they match current filters
        const filteredAlerts = filterAlerts([newAlert]);

        if (filteredAlerts.length > 0) {
          if (isLiveRef.current) {
            setLiveModeAlerts((prev) => [newAlert, ...prev]);
          } else {
            setLocalAlerts((prev) => [newAlert, ...prev]);
          }
        }

        if (Notification.permission === 'granted') {
          new Notification('New Alert', {
            body: newAlert.message || 'You have a new alert!',
          });
        }

        if (newAlert.color === 'red') {
          audioRef.current?.play().catch(console.error);
        }
      }
    },
    [tenantId, type, startDate, endDate]
  );

  // Separate effect for PubNub subscription
  useEffect(() => {
    if (!isRealTimeActive) return;

    const listener = {
      message: ({ message }) => handleNewAlert(message),
    };

    pubnub.addListener(listener);
    pubnub.subscribe({
      channels: ['charger-notification-alert'],
    });

    // Cleanup function
    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({
        channels: ['charger-notification-alert'],
      });
    };
  }, [pubnub, handleNewAlert, isRealTimeActive, tenantId]);

  // Update filters effect
  useEffect(() => {
    const filters = {
      ...(type && type !== 'All' && { alertType: type }),
      ...(startDate &&
        endDate && {
        from: moment(startDate).tz(countryTimezone).utc(),
        to: moment(endDate).tz(countryTimezone).utc(),
      }),
      // ...advanceFilterData,
    };

    getAlerts(filters);
  }, [getAlerts, type, startDate, endDate]);



  // Pagination handler
  const handlePageClick = (page) => {
    const filters = {
      page: page.selected + 1,
      ...(type && type !== 'All' && { alertType: type }),
      ...(startDate &&
        endDate && {
        from: moment(startDate).tz(countryTimezone).utc(),
        to: moment(endDate).tz(countryTimezone).utc(),
      }),
    };
    getAlerts(filters);
  };

  const downloadFile = () => {
    getAlerts({}, true); // true enables excel mode
  };

  // Add transformation function
  const transformAllowedTypes = (types) => {
    if (types.length === 0) return ALERT_TYPES;
    return types.map(type => ({
      label: type,
      value: type
    }));
  };

  useEffect(() => {
    if (isRealTimeActive) {
      const ws = new WebSocket(`${process.env.REACT_APP_LIVE_CHARGING_SESSION}?tenant=${tenantId}`);

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (!data?.id) return;

        handleRealtimeUpdate(data);

        if (data.is_new_alert) {
          setNewAlerts(prev => new Set(prev).add(data.id));
          setTimeout(() => {
            setNewAlerts(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.id);
              return newSet;
            });
          }, 500);
        }
      };

      webSocketRef.current = ws;

      return () => {
        ws.close();
        webSocketRef.current = null;
      };
    }
  }, [isRealTimeActive, handleRealtimeUpdate]);

  // Add update effect
  useEffect(() => {
    if (lastUpdate && isRealTimeActive) {
      // Force re-render of alert list
      setLocalAlerts(prev => [...prev].sort((a, b) => b.timestamp - a.timestamp));
    }
  }, [lastUpdate, isRealTimeActive]);

  // Add filterAlerts function
  const filterAlerts = (alerts) => {
    return alerts.filter(alert => {
      // Filter by type if not 'All' and type is set
      if (type && type !== 'All' && type !== 'All Alerts') {
        // alertType in API may be different from value in dropdown, so compare case-insensitively
        if ((alert.alertType || '').toLowerCase() !== type.toLowerCase() && (alert.type || '').toLowerCase() !== type.toLowerCase()) {
          return false;
        }
      }
      // Filter by date range
      if (startDate && endDate && alert.createdAt) {
        const alertTime = moment(alert.createdAt);
        if (!alertTime.isBetween(moment(startDate), moment(endDate), undefined, '[]')) {
          return false;
        }
      }
      return true;
    });
  };

  return (
    <LayoutContainer>
      <AdminHeader title={t('Alerts')} />
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
        <div className="page-content-wrapper scrollable">
          <div className="notification-details-page__main">
            <Card>
              <Row className='notifications-row'>
                <Col xl={3} lg={3} md={3} style={{ paddingTop: "15px" }}>
                  <Select
                    style={{ width: '100%' }}
                    options={transformAllowedTypes(allowedAlertTypes)}
                    value={{ label: fieldValue, value: type }}
                    onChange={(selected) => {
                      setFieldValue(selected);
                      setType(selected);
                    }}
                    placeholder="Select Alert Type"
                  />

                </Col>
                <Col xl={'auto'} md={'auto'}>
                  <DateTimePicker
                    onChangeOfStartDate={setStartDate}
                    onChangeOfEndDate={setEndDate}
                    initialValueOfStartDate={moment().startOf('day')}
                    initialValueOfEndDate={moment().endOf('day')}
                  />
                </Col>
                <Col xl="auto" md="auto">
                  {isLoader ? (
                    <div className="spinner-wrapper">
                      <Spinner animation="border" variant="success" size="sm" />
                    </div>
                  ) : (
                    <div
                      className="download-icon-wrapper"
                      onClick={downloadFile}
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                    >
                      <div className={`icon icon-excel ${hover ? 'exit' : 'enter'}`}>
                        <SiGooglesheets size={30} color="#5dad5d" />
                      </div>
                      <div className={`icon icon-download ${hover ? 'enter' : 'exit'}`}>
                        <FiDownload size={30} color="#5dad5d" />
                      </div>
                    </div>
                  )}
                </Col>
                <Col xl={2} lg={2} md={2}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Form.Check
                      type="switch"
                      id="realtime-switch"
                      label="Live"
                      style={{ marginTop: '10px' }}
                      checked={isRealTimeActive}
                      onChange={(e) => setIsRealTimeActive(e.target.checked)}
                    />
                    <FiRefreshCw
                      onClick={() => isRealTimeActive && getAlerts({ page: page })}
                      style={{
                        cursor: isRealTimeActive ? 'pointer' : 'not-allowed',
                        marginTop: '10px',
                        marginLeft: '20px',
                        opacity: isRealTimeActive ? 0.5 : 1
                      }}
                      title={isRealTimeActive ? "Disabled during live updates" : "Refresh data"}
                    />
                  </div>
                </Col>
              </Row>
              <hr />
              <div className="general-notification__main">
                {isLoader ? (
                  <div className="loader--block">Loading...</div>
                ) : _.isEmpty(isRealTimeActive ? liveModeAlerts : localAlerts) ? (
                  <div className="empty-data-block">{t('No Data')}</div>
                ) : (
                  filterAlerts(isRealTimeActive ? liveModeAlerts : localAlerts).map((alert, index) => {
                    const alertId = _.get(alert, 'id', '');
                    const updatedAlert = liveUpdates[alertId]
                      ? { ...alert, ...liveUpdates[alertId] }
                      : alert;
                    const isNew = newAlerts.has(alertId);

                    return (
                      <div
                        key={`notification-${index}`}
                        className={isNew ? 'fade-in' : ''}
                      >
                        <AlertItem alert={updatedAlert} />
                      </div>
                    );
                  })
                )}
              </div>
              {!isLoader && !_.isEmpty(isRealTimeActive ? liveModeAlerts : localAlerts) && (
                <ReactPagination
                  currentPage={page}
                  limit={limit}
                  total={totalData}
                  handlePageClick={handlePageClick}
                  totalPages={totalPages}
                  marginPagesDisplayed={1}
                />
              )}
            </Card>
          </div>
        </div>
      </SkeletonTheme>
      {currentUserName !== 'CZ Tenant Admin' && <Footer />}
    </LayoutContainer >
  );
};

export default Alerts;